import type { ReactElement, ReactNode } from "react"
import { useContext } from "react"
import React, { useCallback, useRef } from "react"
import _t from "@core/i18n"
import { getCurrencySign } from "@core/pricing"
import type {
  Benefit,
  ProductHeader as ProductHeaderData,
  SmallBanner,
} from "@onestore-graphql/Model"
import TickCircle from "@onestore-icons/tick-circle.svg"
import get from "lodash/get"
import AccentHeadingAtm from "@onestore/hel/dist/components/atoms/AccentHeadingAtm"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import DividerAtm from "@onestore/hel/dist/components/atoms/DividerAtm"
import IconAtm from "@onestore/hel/dist/components/atoms/IconAtm"
import LabelAtm from "@onestore/hel/dist/components/atoms/LabelAtm"
import LinkAtm from "@onestore/hel/dist/components/atoms/LinkAtm"
import SelectButtonAtm from "@onestore/hel/dist/components/atoms/SelectButtonAtm"
import TagAtm from "@onestore/hel/dist/components/atoms/TagAtm"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import ProductSummaryMol from "@onestore/hel/dist/components/molecules/ProductSummaryMol"
import ProductSummarySimpleMol from "@onestore/hel/dist/components/molecules/ProductSummarySimpleMol"
import TextWithTooltipMol from "@onestore/hel/dist/components/molecules/TextWithTooltipMol"
import TooltipMol from "@onestore/hel/dist/components/molecules/TooltipMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BorderOrg from "@onestore/hel/dist/components/organisms/BorderOrg"
import ContainerOrg from "@onestore/hel/dist/components/organisms/ContainerOrg"
import ContentWithAsideOrg from "@onestore/hel/dist/components/organisms/ContentWithAsideOrg"
import ContentWithFloatingAsideOrg from "@onestore/hel/dist/components/organisms/ContentWithFloatingAsideOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import GridOrg from "@onestore/hel/dist/components/organisms/GridOrg"
import ProductHeaderOrg from "@onestore/hel/dist/components/organisms/ProductHeaderOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import SectionOrg from "@onestore/hel/dist/components/organisms/SectionOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import WrapOrg from "@onestore/hel/dist/components/organisms/WrapOrg"
import Picture from "@onestore/hel/dist/components/quarks/Picture"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import { BasketActionSource } from "@gatsby-plugin-basket/store/actions"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import MarkdownTile from "@gatsby-plugin-generic-page/components/Markdown/Tile"
import Media from "@gatsby-plugin-generic-page/components/Media"
import getBaseMarkdownOverrides from "@gatsby-plugin-generic-page/helpers/getBaseMarkdownOverrides"
import LowestPriceInfo from "~/components/LowestPriceInfo"
import ProductBoxPriceOldVersion from "~/components/ProductBoxPriceOldVersion"
import HeaderContext from "~/context/HeaderContext"
import type { EmbeddedAssetSquidexMetadata } from "~/fragments/image"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import useHeaderHeightSpace from "~/hooks/useHeaderHeightSpace"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import useSaleConfigurationState from "~/hooks/useSaleConfigurationState"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"
import { useAppDispatch } from "~/store/hooks"
import { COMPONENT_ID } from "~/types/component"
import type { ProductBreadcrumbItem, ProductBreadcrumbs } from "~/types/Product"
import { saleConfigurationValueChange } from "../../store/actions"
import ProductCostCalculator from "../ProductCostCalculator"
import SmallBannerBox from "../SmallBannerBox"
import { CostCalculatorModalButton } from "./CostCalculatorModalButton"
import { PrimaryButton } from "./PrimaryButton"
import { SecondaryButton } from "./SecondaryButton"
import useSummary from "./useSummary"
import useWidgets from "./useWidgets"

interface ProductHeaderProps {
  productHeader: ProductHeaderData
  productBreadcrumbs: ProductBreadcrumbs
  saleConfiguration: SaleConfiguration
  features?: ReactNode
  bundle?: ReactNode
  detailedTitle?: string
  description?: ReactNode
  hasCompareProducts?: boolean
  benefits?: Benefit[]
  banner?: SmallBanner[]
  badge?: {
    image: EmbeddedAssetSquidexMetadata[]
    text: string
  }
}

interface BreadcrumbItem {
  title: string
  text: string
  to: string
}

export default function ProductHeader({
  productBreadcrumbs,
  productHeader,
  saleConfiguration,
  features,
  bundle,
  detailedTitle,
  description,
  hasCompareProducts = false,
  benefits,
  banner,
  badge,
}: ProductHeaderProps): ReactElement<ProductHeaderProps> {
  const contentRef = useRef<HTMLDivElement>(null)
  const isMediumScreenOnly = useBreakpoint("medium", "large")
  const isMediumScreenUp = useBreakpoint("medium")
  const isLargeScreenUp = useBreakpoint("large")

  const { hasHeaderBottomBar } = useContext(HeaderContext)

  const breadcrumbs = productBreadcrumbs.items.map(
    (item: ProductBreadcrumbItem): BreadcrumbItem => ({
      text: item.name,
      title: item.name,
      to: item.src,
    })
  )

  const saleConfigurationState = useSaleConfigurationState(
    saleConfiguration?.id || ""
  )

  const dispatch = useAppDispatch()

  const onOptionValueSelect = useCallback(
    (name, value) => {
      dispatch(
        saleConfigurationValueChange(saleConfiguration?.id || "", name, value)
      )
    },
    [dispatch, saleConfiguration?.id]
  )

  const widgets = useWidgets(
    saleConfigurationState?.controls,
    saleConfigurationState?.options,
    onOptionValueSelect,
    saleConfiguration?.flatData.connectedProducts,
    saleConfiguration?.flatData.connectedControlsTitle
  )

  const summary = useSummary(saleConfiguration)

  const hasLightBox = !!productHeader.headerLogoMedium

  const headerLogo = {
    image: {
      src: productHeader.headerLogo ? productHeader.headerLogo.url : "",
      alt: productHeader.headerLogo ? productHeader.headerLogo.fileName : "",
      height: productHeader.headerLogo.height,
      width: productHeader.headerLogo.width,
      title: productHeader.headerLogo.title,
    },
  }
  const headerLogoMedium = {
    image: {
      src: productHeader.headerLogoMedium
        ? productHeader.headerLogoMedium.url
        : "",
      alt: productHeader.headerLogoMedium
        ? productHeader.headerLogoMedium.fileName
        : "",
      height: productHeader.headerLogoMedium?.height,
      width: productHeader.headerLogoMedium?.width,
      title: productHeader.headerLogoMedium?.title,
    },
  }
  const logo = hasLightBox ? headerLogoMedium : headerLogo

  const tags = productHeader.badges.map((badge) => badge.flatData.text)

  const tagsWrapperJsx = (
    <PushOrg bottomSpace={3}>
      <WrapOrg gutterSpace={1}>
        {tags.map((tag) => (
          <TagAtm key={tag}>{tag}</TagAtm>
        ))}
      </WrapOrg>
    </PushOrg>
  )

  const titleJsx = (
    <TextAtm typography="great" htmlTag="h1" pushSpace={2}>
      {productHeader.name}
    </TextAtm>
  )

  const detailsJsx = (
    <MarkdownText
      overrides={getBaseMarkdownOverrides({
        typography: "medium1_h",
        pushSpace: 2.5,
        boldTypography: "medium4_h",
      })}
      forceBlock={false}
    >
      {productHeader.details}
    </MarkdownText>
  )

  const getCostCalculatorElement = (
    addCartSource?: BasketActionSource
  ): ReactElement | null => {
    if (
      !isEmpty(saleConfiguration.flatData.costCalculator) &&
      !isEmpty(saleConfiguration.flatData.defaultCloudBluePlan) &&
      !isEmpty(saleConfiguration.flatData.defaultCloudBluePlan[0].flatData)
    ) {
      return (
        <ProductCostCalculator
          saleConfiguration={saleConfiguration}
          defaultCloudBluePlanData={
            saleConfiguration.flatData.defaultCloudBluePlan[0].flatData
          }
          calculatorData={saleConfiguration.flatData.costCalculator}
          addCartSource={addCartSource}
        />
      )
    }

    return null
  }

  const costCalculatorElement = getCostCalculatorElement()

  const configJsx = widgets && (
    <PushOrg topSpace={isMediumScreenUp ? 4 : 3}>
      <WrapOrg gutterSpace={2}>
        {widgets.map((configValues, index) => {
          const configValuesJsx = (
            <WrapOrg gutterSpace={1} {...configValues.data}>
              {configValues.options.map((option) => (
                <SelectButtonAtm
                  onClick={option.onClick}
                  isActive={option.isActive}
                  key={`${configValues.title}-${option.text}`}
                >
                  {option.text}
                </SelectButtonAtm>
              ))}
            </WrapOrg>
          )

          const configValuesWithCalculatorButtonJsx = (
            <GridOrg
              gutterSpace={{ x: 7, y: 4 }}
              columns={{
                large: 2,
                medium: 1,
                small: 1,
              }}
            >
              <WrapOrg gutterSpace={1} {...configValues.data}>
                {configValues.options.map((option) => (
                  <SelectButtonAtm
                    onClick={option.onClick}
                    isActive={option.isActive}
                    key={`${configValues.title}-${option.text}`}
                  >
                    {option.text}
                  </SelectButtonAtm>
                ))}
              </WrapOrg>

              <div>
                <CostCalculatorModalButton
                  costCalculatorElement={getCostCalculatorElement(
                    BasketActionSource.MODAL
                  )}
                />
              </div>
            </GridOrg>
          )

          return (
            <div key={configValues.title}>
              <TextAtm typography="nano2" pushSpace={1}>
                {configValues.title}
              </TextAtm>

              {index === 1 && !isEmpty(costCalculatorElement)
                ? configValuesWithCalculatorButtonJsx
                : configValuesJsx}
            </div>
          )
        })}
      </WrapOrg>
    </PushOrg>
  )

  const anchorJsx = hasCompareProducts && (
    <PushOrg topSpace={3}>
      <LinkAtm
        title={_t("marketplace.compare")}
        scrollTo={COMPONENT_ID.MARKETPLACE_COMPARE}
      >
        {_t("marketplace.compare")}
      </LinkAtm>
    </PushOrg>
  )

  const featuresJsx = features && <PushOrg topSpace={5}>{features}</PushOrg>

  const bundleJsx = bundle && (
    <PushOrg topSpace={isMediumScreenUp ? 5 : 3}>
      <div id={COMPONENT_ID.PRODUCT_BUNDLE}>
        <PushOrg bottomSpace={4}>
          <AccentHeadingAtm>{_t("marketplace.bundleBuy")}</AccentHeadingAtm>
        </PushOrg>

        {bundle}
      </div>
    </PushOrg>
  )

  const descriptionJsx = (
    <PushOrg topSpace={isMediumScreenUp ? 5 : 3}>
      <PushOrg bottomSpace={4}>
        <AccentHeadingAtm>
          {detailedTitle || _t("marketplace.detailedDescription")}
        </AccentHeadingAtm>
      </PushOrg>

      {description}
    </PushOrg>
  )

  const summaryBenefitsJsx = benefits ? (
    <GridOrg columns={1} gutterSpace={2}>
      {benefits?.map((benefit) => {
        const { text, image, description } = benefit.flatData

        const mediaJSX = !isEmpty(image) ? (
          <WidthOrg widthSpace={2}>
            <Media media={image} />
          </WidthOrg>
        ) : null

        const imageJSX = mediaJSX || <IconAtm icon={TickCircle} sizeSpace={2} />

        return (
          <FlexContainerOrg alignItems="center" key={benefit.id}>
            <PushOrg rightSpace={2}>{imageJSX}</PushOrg>

            <TextWithTooltipMol
              text={text}
              typography="small2"
              tooltip={description}
            />
          </FlexContainerOrg>
        )
      })}
    </GridOrg>
  ) : null

  const { priceText: customPriceText, formilyForm } =
    saleConfiguration?.flatData.customPriceBox || {}

  const customPriceTextJSX = (
    <TextAtm typography="large2" pushSpace={{ top: 3, bottom: 3 }}>
      {customPriceText}
    </TextAtm>
  )

  const { showDetailedPrices } = usePriceConfigurationQuery()
  const periodInfo = summary?.selectedPeriodInfo || summary?.periodInfo
  const priceInfo =
    summary?.selectedPriceInfo ||
    get(
      saleConfiguration,
      "flatData.defaultCloudBluePlan[0].flatData.priceInfo"
    )

  const detailedPriceJSX =
    showDetailedPrices && !isEmpty(periodInfo) ? (
      <DetailedPrice
        variant={{ type: PriceVariantType.LARGE, align: "left" }}
        periodInfo={periodInfo}
        priceInfo={priceInfo}
      />
    ) : null

  const priceJSX = !isEmpty(summary) ? (
    <ProductBoxPriceOldVersion
      hasPromotion={summary.hasPromotion}
      value={`${summary.priceBig!.integer}${
        summary.priceBig!.fraction
      } ${getCurrencySign()}`}
      suffix={`${summary.priceBig!.suffix}`}
      tooltip={
        summary.tooltip ? (
          <TooltipMol iconSize="large">
            <MarkdownTile>{summary.tooltip}</MarkdownTile>
          </TooltipMol>
        ) : undefined
      }
      additionalText={
        summary.hasPromotion ? (
          <PushOrg topSpace={1}>
            <LowestPriceInfo
              value={summary.lowestPrice}
              percent={summary.lowestPricePercent}
            />
          </PushOrg>
        ) : undefined
      }
    />
  ) : null

  const summaryJsx = !isEmpty(summary) ? (
    <ProductSummaryMol
      label={
        summary.hasPromotion ? (
          <LabelAtm size="small" variant="primaryOutlined" emphasis="high">
            {_t("marketplace.discountTitle")}
          </LabelAtm>
        ) : !isEmpty(summary.label) ? (
          <LabelAtm size="small" variant={summary.label?.variant}>
            {summary.label?.text}
          </LabelAtm>
        ) : undefined
      }
      price={
        customPriceText ? customPriceTextJSX : detailedPriceJSX || priceJSX
      }
      primaryButton={
        <PrimaryButton
          saleConfiguration={saleConfiguration}
          formilyForm={customPriceText ? formilyForm : undefined}
        />
      }
      secondaryButton={
        !customPriceText && !isEmpty(formilyForm) ? (
          <SecondaryButton formilyForm={formilyForm} />
        ) : !!bundle ? (
          <SecondaryButton />
        ) : undefined
      }
      footer={summaryBenefitsJsx}
    />
  ) : (
    <ProductSummaryMol
      price={
        <PushOrg topSpace={2} hasNoReset hasForcedFullWidth>
          <ButtonAtm
            size="big"
            title={_t("marketplace.unavailable")}
            isExpanded
            isDisabled
          >
            {_t("marketplace.unavailable")}
          </ButtonAtm>
        </PushOrg>
      }
      primaryButton={<></>}
      footer={summaryBenefitsJsx}
    />
  )

  const summarySimpleJsx = summary && (
    <ProductSummarySimpleMol
      price={summary.priceBig}
      button={<PrimaryButton simple saleConfiguration={saleConfiguration} />}
    />
  )

  const contactBoxJsx = banner && banner.length > 0 && (
    <SmallBannerBox
      text={banner[0].flatData.text}
      image={banner[0].flatData.images}
    />
  )

  const headerBadgeJsx = badge ? (
    <FlexContainerOrg alignItems="center" gapSpace={2.5}>
      <PushOrg
        topSpace={1}
        rightSpace={1}
        bottomSpace={1}
        leftSpace={1}
        hasNoReset
      >
        <Picture srcData={assetsSquidexToImageObject(badge.image, "")} />
      </PushOrg>

      <WidthOrg widthSpace={27.5}>
        <BorderOrg
          border={{
            left: {
              style: "solid",
              width: 1,
              emphasis: "low",
            },
          }}
          onColor="background"
        >
          <PushOrg leftSpace={3} hasNoReset>
            <MarkdownText
              overrides={getBaseMarkdownOverrides({
                typography: "xsmall1",
                boldTypography: "small4",
                emphasis: "medium",
              })}
            >
              {badge?.text}
            </MarkdownText>
          </PushOrg>
        </BorderOrg>
      </WidthOrg>
    </FlexContainerOrg>
  ) : null

  const mobileHeaderBadgeJsx = badge ? (
    <div>
      <AlignContentOrg horizontalAlign="center">
        <PushOrg topSpace={1} bottomSpace={2}>
          <Picture srcData={assetsSquidexToImageObject(badge.image, "")} />
        </PushOrg>

        <MarkdownText
          overrides={getBaseMarkdownOverrides({
            typography: "xsmall1",
            boldTypography: "small4",
            emphasis: "medium",
          })}
        >
          {badge?.text}
        </MarkdownText>
      </AlignContentOrg>

      <DividerAtm marginSpace={3} emphasis="low" />
    </div>
  ) : null

  const summaryWrapperJsx = isMediumScreenUp ? (
    <>
      <PushOrg bottomSpace={2}>{summaryJsx}</PushOrg>

      {contactBoxJsx}
    </>
  ) : (
    <>
      <DividerAtm marginSpace={3} emphasis="low" />
      <PushOrg bottomSpace={2}>{summaryJsx}</PushOrg>
      {contactBoxJsx}
      <DividerAtm marginSpace={3} emphasis="low" />
      {mobileHeaderBadgeJsx}
    </>
  )

  const costCalculatorJsx = !isEmpty(costCalculatorElement) ? (
    <PushOrg topSpace={isMediumScreenUp ? 5 : 3}>
      <div id={COMPONENT_ID.COST_CALCULATOR}>
        <PushOrg bottomSpace={4}>
          <AccentHeadingAtm>
            {_t("marketplace.costCalculator")}
          </AccentHeadingAtm>
        </PushOrg>

        {costCalculatorElement}
      </div>
    </PushOrg>
  ) : null

  const contentJsx = (
    <>
      <div ref={contentRef}>
        {tagsWrapperJsx}
        {titleJsx}
        {!isMediumScreenUp && summaryWrapperJsx}
      </div>
      {isLargeScreenUp ? detailsJsx : null}
      {configJsx}
      {isMediumScreenUp ? anchorJsx : null}
      {isLargeScreenUp ? featuresJsx : null}
      {isLargeScreenUp ? bundleJsx : null}
      {isLargeScreenUp ? costCalculatorJsx : null}
      {isLargeScreenUp ? descriptionJsx : null}
    </>
  )

  const asideJsx = isMediumScreenUp ? (
    <>{summaryWrapperJsx}</>
  ) : (
    <>{summarySimpleJsx}</>
  )

  const headerHeightSpace = useHeaderHeightSpace()

  return (
    <SectionOrg spacing="small" color="light" id={COMPONENT_ID.PRODUCT_HERO}>
      <ContainerOrg>
        <PushOrg bottomSpace={4}>
          <ProductHeaderOrg
            backgroundImage={{
              small: productHeader.headerBgSmall
                ? productHeader.headerBgSmall.url
                : "",
              medium: productHeader.headerBgMedium
                ? productHeader.headerBgMedium.url
                : "",
              large: productHeader.headerBgLarge
                ? productHeader.headerBgLarge.url
                : "",
            }}
            logo={logo}
            breadcrumbs={{
              items: breadcrumbs,
              currentItem: productBreadcrumbs.current,
            }}
            logoZoomLightBox={
              hasLightBox
                ? {
                    openText: _t("marketplace.logo.zoom.in"),
                    closeText: _t("marketplace.logo.zoom.out"),
                  }
                : undefined
            }
            badge={headerBadgeJsx}
          />
        </PushOrg>

        {isMediumScreenOnly ? (
          <ContentWithAsideOrg
            content={contentJsx}
            aside={asideJsx}
            asideWidthSpace={28}
            gutterSizeSpace={4}
            asideCollapseBreakpoint="medium"
          />
        ) : (
          <ContentWithFloatingAsideOrg
            mobileFixedAsideBreakpointRef={contentRef}
            content={contentJsx}
            aside={asideJsx}
            asidePushTopSpace={headerHeightSpace}
            asideWidthSpace={isMediumScreenOnly ? 28 : undefined}
            asideFloatingStartBreakpoint="medium"
            hasHeaderBottomBar={hasHeaderBottomBar}
          />
        )}
      </ContainerOrg>
    </SectionOrg>
  )
}
